import { FieldErrors, FieldValues, UseFormReturn } from 'react-hook-form';
import uniqBy from 'lodash/uniqBy';

import { logError } from 'services/logging/logging';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { GenericError } from 'support/helpers/errors/errors';

import { getAllWebEDIErrors } from './utils';
import { remapErrorsToFirstCell } from './remapErrors';

/**
 * `handleSubmit` that still submits when only hidden errors remain. The user cannot fix a hidden error,
 * so blocking on it blocks the send for good; we log it after the send instead.
 */
export const handleSubmitAllowingHiddenErrors = <TFieldValues extends FieldValues>(
  { handleSubmit, getValues }: Pick<UseFormReturn<TFieldValues>, 'handleSubmit' | 'getValues'>,
  onValid: (values: TFieldValues) => void,
) =>
  handleSubmit(onValid, (errors) => {
    if (hasOnlyHiddenErrors(errors)) {
      onValid(getValues());
    }
  });

export const logSentWithHiddenErrors = (transaction: { id: string | undefined; type: string }) => {
  const { hiddenErrors } = useWebEDIStore.getState();
  if (hiddenErrors.length === 0) {
    return;
  }
  logError(
    new GenericError('[CS] WebEDI: document sent with hidden errors', {
      transactionId: transaction.id,
      transactionType: transaction.type,
      hiddenErrors: uniqBy(hiddenErrors, 'fieldPath').map(({ fieldPath, title }) => ({ fieldPath, title })),
    }),
  );
};

/**
 * True when the form has errors and the user can see none of them: each one is flagged hidden by the
 * process specification, or belongs to a section the uiConfig does not render.
 */
const hasOnlyHiddenErrors = (formErrors: FieldErrors): boolean => {
  const { uiConfig } = useWebEDIStore.getState();
  // Before the uiConfig loads, every error looks like it belongs to a missing section.
  if (uiConfig.length === 0) {
    return false;
  }
  const errors = Object.values(remapErrorsToFirstCell(getAllWebEDIErrors(formErrors, {}), uiConfig).remappedErrors);
  return errors.length > 0 && errors.every((error) => error.type === 'hidden');
};
